import {useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Tag} from '@/components/ui/tag'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {DataTable, type Column} from '@/components/data-table'
import {EntitySearchSelect} from '@/components/entity-search-select'
import {HelpIcon} from '@/components/help-icon'
import {useTableState} from '@/hooks/common/use-table-state'
import {ordenarCandidatos, type PersonaDelMapeo} from '@/lib/mapeo-archivos'
import {normalize} from '@/lib/utils'
import {tope} from '@/lib/campos-texto'

const POR_PAGINA = 20

type FiltroEstado = 'todas' | 'con' | 'sin'
const FILTROS: FiltroEstado[] = ['todas', 'con', 'sin']

type OpcionArchivo = {nombre: string; ocupadoPor: string | null}

export function MapeoEscaneosTabla({
  personas,
  archivos,
  mapeo,
  propuestos,
  onCambiar,
}: {
  personas: PersonaDelMapeo[]
  archivos: string[]
  mapeo: Record<string, string>
  /** Los `paquete_id` cuyo valor lo puso el sistema y nadie ha tocado todavía. */
  propuestos: Set<string>
  onCambiar: (paqueteId: string, archivo: string) => void
}) {
  const {t} = useTranslation()
  const {page, setPage, searchInput, setSearchInput, search} = useTableState('persona')
  const [estado, setEstado] = useState<FiltroEstado>('todas')

  const duenoPorArchivo = useMemo(() => {
    const dueno = new Map<string, string>()
    for (const persona of personas) {
      const archivo = mapeo[persona.id]
      if (archivo) dueno.set(archivo, persona.nombre)
    }
    return dueno
  }, [personas, mapeo])

  const filtradas = useMemo(() => {
    const consulta = normalize(search)
    return personas.filter((persona) => {
      if (consulta && !normalize(`${persona.nombre} ${persona.cedula}`).includes(consulta)) return false
      if (estado === 'todas') return true
      return estado === 'con' ? !!mapeo[persona.id] : !mapeo[persona.id]
    })
  }, [personas, search, estado, mapeo])

  // Cambiar un archivo puede vaciar la página actual (filtro «sin archivo»): se acota para no dejarla vacía.
  const paginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA))
  const paginaSegura = Math.min(page, paginas)
  const visibles = filtradas.slice((paginaSegura - 1) * POR_PAGINA, paginaSegura * POR_PAGINA)

  function cambiarFiltro(siguiente: FiltroEstado) {
    setEstado(siguiente)
    setPage(1)
  }

  const columnas: Column<PersonaDelMapeo>[] = [
    {
      key: 'persona',
      header: t('admin.bateria.escaneos.mapeoColPersona'),
      width: 'w-[40%]',
      cell: (persona) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-foreground">{persona.nombre}</p>
          <p className="font-mono text-[11px] text-muted-foreground">{persona.cedula}</p>
        </div>
      ),
    },
    {
      key: 'archivo',
      // T3: el criterio del chip «Propuesto» se explica una vez, en la cabecera de la columna.
      header: (
        <span className="inline-flex items-center gap-1.5">
          {t('admin.bateria.escaneos.mapeoColArchivo')}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('admin.bateria.escaneos.mapeoPropuesto')})}
            testId="manifest-propuesto-tip">
            {t('admin.bateria.escaneos.mapeoPropuestoTip')}
          </HelpIcon>
        </span>
      ),
      cell: (persona) => (
        <CeldaArchivo
          persona={persona}
          archivos={archivos}
          valor={mapeo[persona.id] ?? ''}
          propuesto={propuestos.has(persona.id)}
          duenoPorArchivo={duenoPorArchivo}
          onCambiar={(archivo) => onCambiar(persona.id, archivo)}
        />
      ),
    },
  ]

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          maxLength={tope('BUSCA')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder={t('admin.bateria.escaneos.mapeoBuscar')}
          className="max-w-[16rem]"
          data-testid="mapeo-buscar"
        />
        <div className="flex items-center gap-1">
          {FILTROS.map((filtro) => (
            <Button
              key={filtro}
              type="button"
              size="xs"
              variant={estado === filtro ? 'default' : 'outline'}
              data-testid={`mapeo-filtro-${filtro}`}
              onClick={() => cambiarFiltro(filtro)}>
              {t(`admin.bateria.escaneos.mapeoFiltro.${filtro}`)}
            </Button>
          ))}
        </div>
      </div>
      <DataTable
        columns={columnas}
        rows={visibles}
        rowKey={(persona) => persona.id}
        emptyState={
          <p className="py-6 text-center text-sm text-muted-foreground" data-testid="mapeo-vacio">
            {t('admin.bateria.escaneos.mapeoVacio')}
          </p>
        }
      />
      {filtradas.length > POR_PAGINA && (
        <PaginationBar page={paginaSegura} pageSize={POR_PAGINA} total={filtradas.length} onPageChange={setPage} />
      )}
    </div>
  )
}

/** Componente y no un `cell` para memorizar su orden: puntuar el ZIP en ~500 filas por tecla es caro. */
function CeldaArchivo({
  persona,
  archivos,
  valor,
  propuesto,
  duenoPorArchivo,
  onCambiar,
}: {
  persona: PersonaDelMapeo
  archivos: string[]
  valor: string
  propuesto: boolean
  duenoPorArchivo: Map<string, string>
  onCambiar: (archivo: string) => void
}) {
  const {t} = useTranslation()
  const ordenados = useMemo(() => ordenarCandidatos(persona, archivos), [persona, archivos])
  const opciones: OpcionArchivo[] = useMemo(
    () =>
      ordenados.map((nombre) => ({
        nombre,
        ocupadoPor: duenoPorArchivo.get(nombre) === persona.nombre ? null : (duenoPorArchivo.get(nombre) ?? null),
      })),
    [ordenados, duenoPorArchivo, persona.nombre]
  )

  return (
    <div className="flex min-w-0 items-center gap-2">
      <div className="min-w-0 flex-1">
        <EntitySearchSelect
          items={opciones}
          value={valor ? {nombre: valor, ocupadoPor: null} : null}
          getKey={(opcion) => opcion.nombre}
          filterText={(opcion) => opcion.nombre}
          renderOption={(opcion) => (
            <span className="block min-w-0">
              <span className="block font-mono text-xs break-all">{opcion.nombre}</span>
              {opcion.ocupadoPor && (
                <span className="block text-[11px] text-muted-foreground">
                  {t('admin.bateria.escaneos.mapeoOcupado', {persona: opcion.ocupadoPor})}
                </span>
              )}
            </span>
          )}
          renderTriggerValue={(opcion) => <span className="font-mono text-xs">{opcion.nombre}</span>}
          placeholder={t('admin.bateria.escaneos.mapeoSinArchivo')}
          searchPlaceholder={t('admin.bateria.escaneos.mapeoBuscarArchivo')}
          noResultsLabel={t('admin.bateria.escaneos.mapeoSinCoincidencias')}
          clearLabel={t('admin.bateria.escaneos.mapeoQuitar')}
          freeTextLabel={(texto) => t('admin.bateria.escaneos.mapeoUsarTexto', {texto})}
          onFreeText={onCambiar}
          testId={`manifest-${persona.id}`}
          onPick={(opcion) => onCambiar(opcion?.nombre ?? '')}
        />
      </div>
      {propuesto && (
        <Tag data-testid={`manifest-propuesto-${persona.id}`}>{t('admin.bateria.escaneos.mapeoPropuesto')}</Tag>
      )}
    </div>
  )
}
